import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  EntityServiceClient,
  RawMaterialDto,
  RawMaterialFilterDto,
  RawMaterialSemiAutoLineDto,
  RawMaterialService,
  ReferenceDto,
  ReferenceFilterDto,
  ReferenceServiceClient
} from '@sharedcommon/rest/data.restservice';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@sharedcommon/services/user.service';
import { User } from '@sharedcommon/model/user.model';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@sharedcommon/services/logger.service';
import { Constants } from '@sharedcommon/util/constants.util';
import { ToastService } from '@sharedcommon/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HeaderService } from '@sharedcommon/services/header.service';
import { EntityDto, EntityFilterDto, SubEntityServiceClient } from '@sharedcommon/rest/identity.restservice';
import { ValidationService } from '@sharedcommon/services/validation.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NeqAdvancedReferencesSearchPopinComponent } from '@usercommon/components/advanced-references-search/advanced-references-search-popin.component';
import { of, Subscription } from 'rxjs';
import { NeqAbstractFormViewComponent } from '@sharedcommon/components/validation/abstract-form-view.component';
import { FormErrorsMessagesModel } from '@sharedcommon/model/form-errors-messages.model';
import { MatDialog } from '@angular/material/dialog';
import { RawmaterialOrSampleService } from '@usercommon/service/rawmaterial-or-sample.service';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { ImportSemiAutoStore } from '@usercommon/stores/import-semiauto.store';
import { URN } from '@sharedcommon/model/urn.model';
import { CalculationStatus } from "@sharedcommon/model/calculation-status.model";
import {RawmaterialOrSampleStatus} from '@sharedcommon/util/rawmaterial-or-sample-status.utils';

/* Same number user in duplication of reference in back
 * which is a directive from RG2 of US with the ID ALPVL1B-42 */
const MAX_LENGTH_FOR_CODE_WHEN_DUP = 20;

@Component({
  selector: 'neq-rawmaterials-properties',
  templateUrl: './rawmaterials-properties.page.html'
})
export class NeqRawmaterialsPropertiesPage extends NeqAbstractFormViewComponent implements OnInit, OnDestroy, AfterViewInit {

  readonly ERROR_READ_ONLY = 'readonly';

  /**
   * Trigger the check of submit error
   */
  check = false;
  /**
   * List of possible error message for the Reference field
   */
  messageListReference: FormErrorsMessagesModel[] = [
    { message: 'error.common.field-required', errorType: 'required', show: false }
  ];

  public references: ReferenceDto[];
  user: User;
  isEditing: boolean;
  private idToEdit: string;
  selected: ReferenceDto;
  rawMaterial: RawMaterialDto;
  entity: EntityDto;
  entityLogo: SafeUrl;
  canUpdateEntity = false;
  public isReadOnly = true;
  public error: any;
  private subscriptionForUrlParams: Subscription;
  private readonly isDuplicating: boolean = false;
  private readonly idToDuplicate: string;
  private isReferenceModified: boolean;

  rawmaterialForm = new FormGroup({
    code: new FormControl(null, [
      Validators.required,
      Validators.pattern(Constants.REGEX_ALL_EXCEPT_SPACES_AND_GENERIC_CURRENCY_SYMBOL),
      Validators.maxLength(Constants.MAX_LENGTH_FOR_RM_CODE)
    ]),
    label: new FormControl(null, [Validators.required, Validators.maxLength(Constants.MAX_LENGTH_FOR_RM_LABEL)]),
    reference: new FormControl(null, Validators.required),
    entityCode: new FormControl(null, Validators.required),
    notes: new FormControl()
  });
  private subscriptionForSemiautoCurrentRm: Subscription;
  private subscriptionForSemiAutoState: Subscription;
  private importSemiAutoIsOnGoing: boolean = false;
  /** Indicates if we arrived here from input window (and not a semi auto navigation) */
  private fromInputPage: boolean;

  constructor(
    private readonly entityService: EntityServiceClient,
    private readonly subEntityService: SubEntityServiceClient,
    private readonly referenceService: ReferenceServiceClient,
    private readonly rawMatService: RawMaterialService,
    private readonly userService: UserService,
    public toastService: ToastService,
    private readonly router: Router,
    private readonly store: RawmaterialOrSampleService,
    private readonly route: ActivatedRoute,
    private readonly logger: LoggerService,
    private readonly translate: TranslateService,
    private readonly headerService: HeaderService,
    private readonly domSanitizer: DomSanitizer,
    private readonly dialogMat: MatDialog,
    protected validation: ValidationService,
    private readonly importSemiautoStore: ImportSemiAutoStore
  ) {
    super(validation);
    this.fromInputPage = this.router.getCurrentNavigation().extras.state?.fromInput;
    this.validationService.init();
    this.checkInForm(this.rawmaterialForm);
    this.user = this.userService.getUser();
    this.canUpdateEntity = this.userService.hasRights(['U_MP_CHANGE_ENTITY']);
    this.idToDuplicate = this.store.getProductIdToDuplicate();
    this.isDuplicating = !!this.idToDuplicate;
  }

  async ngOnInit() {
    this.references = await this.entityService.referencesFetch(this.userService.getEntity(), new ReferenceFilterDto())
      .pipe(map(x => x.data), catchError(_e => of([])))
      .toPromise();

    if (this.importSemiautoStore.isProcessIsOnGoing()) {
      // Initialized component when current imported RM with semi auto context.
      await this.initSemiautoImport();
    } else {
      this.subscriptionForUrlParams = this.route.params
        .subscribe(params => {
          /* If we are in duplicating mode then do not take into account the id from path (even if present) */
          this.idToEdit = this.isDuplicating ? null : params['id'];
          this.isEditing = FunctionUtils.isDefined(this.idToEdit);
          this.updateMetaDatasOnInit();
        });
    }
  }

  async initSemiautoImport() {
    this.subscriptionForSemiautoCurrentRm = this.importSemiautoStore.getCurrentRm()
      .subscribe((rm: RawMaterialSemiAutoLineDto) => {
        if (rm != null) {
          if (this.fromInputPage) {
            this.isEditing = true;
            this.idToEdit = rm.rawMaterial.id;
            this.updateMetaDatasOnInit();
            /* For the next event raw mat in semi auto process it will not trig this initialization part */
            this.fromInputPage = false;
          } else {
            this.updateFormFromSemiAutoProcess(rm.rawMaterial);
          }
        }
      });

    this.subscriptionForSemiAutoState = this.importSemiautoStore.getSemiAutoProcessState()
      .subscribe(state => this.importSemiAutoIsOnGoing = state);
  }

  private async updateFormFromSemiAutoProcess(rawMaterial: RawMaterialDto): Promise<void> {
    this.retrieveSubEntityByCode(rawMaterial.entity.identifier);

    if (rawMaterial.referenceId != null && rawMaterial.referenceId !== '') {
      await this.retrieveReferenceOfRawMat(rawMaterial);
    } else {
      this.selected = null;
    }

    if (this.selected === null) {
      this.rawmaterialForm.get('reference').reset();
    } else {
      this.rawmaterialForm.get('reference').setValue(this.selected);
    }

    this.idToEdit = rawMaterial.id;
    this.isEditing = !!this.idToEdit;
    this.rawMaterial = this.isEditing ? rawMaterial : null;
    this.updateHeaderOfPage(this.rawMaterial, false);

    this.rawmaterialForm.get('label').setValue(rawMaterial.label);
    this.rawmaterialForm.get('code').setValue(rawMaterial.code);

    this.rawmaterialForm.get('reference').markAsDirty();
    this.rawmaterialForm.get('label').markAsDirty();
    this.rawmaterialForm.get('code').markAsDirty();
    this.rawmaterialForm.markAsDirty();
    if (this.isEditing) {
      /* Lock the rawMat only if we are in editing mode */
      this.patchRawMatToLockItWhenEditionMode(this.rawMaterial);
    }
  }

  ngOnDestroy() {
    this.subscriptionForUrlParams?.unsubscribe();

    if (this.userService.getEntity() && !this.isReadOnly && this.isEditing) {
      this.rawMatService.patch(this.userService.getEntity(), {patchAction: 'UNLOCK_ENTITY', rawMatIds: Array.of(this.idToEdit)}).subscribe();
    }
    /* Reset it to avoid to duplicate when we go back to this component */
    this.store.setProductIdToDuplicate(null);

    this.subscriptionForSemiautoCurrentRm?.unsubscribe();
    this.subscriptionForSemiAutoState?.unsubscribe();
  }

  /**
   * Update the meta on init
   */
  updateMetaDatasOnInit(): void {
    const idToRequest = this.idToDuplicate || this.idToEdit;
    if (idToRequest) {
      this.rawMatService.get(this.userService.getEntity(), idToRequest)
        .subscribe((data) => {
          this.rawMaterial = data;
          this.retrieveSubEntityByCode(this.rawMaterial.entity.identifier);
          this.updateHeaderOfPage(data, true);
          this.updateFormWithRetrievedRawMat(data);
          this.retrieveReferenceOfRawMat(data);
          if (this.isEditing) {
            /* Lock the rawMat only if we are in editing mode */
            this.patchRawMatToLockItWhenEditionMode(data);
          } else if (this.isDuplicating) {
            /* To show to the user that he has to update the code */
            this.rawmaterialForm.get("code").markAsTouched();
            this.isReadOnly = false;
          }
        });
    } else {
      this.isReadOnly = false;
      this.updateHeaderOfPage(null, false);
      this.retrieveSubEntityByCode(this.userService.getEntity());
    }
  }

  /**
   * Update the form data according raw material
   * @param rawMat the RawMaterial which contains the data to set in the form
   */
  private updateFormWithRetrievedRawMat(rawMat: RawMaterialDto): void {
    const dataToPatch = {...rawMat};
    dataToPatch.label = this.isDuplicating ?
      /* If in duplicated mode then add prefix to the label */
      this.addPrefixAndTrimIfTooLong(rawMat.label, '[DUP]', Constants.MAX_LENGTH_FOR_RM_LABEL, '...')
      : rawMat.label;
    dataToPatch.code = this.isDuplicating ?
                            this.addPrefixAndTrimIfTooLong(rawMat.id, '¤', MAX_LENGTH_FOR_CODE_WHEN_DUP, '')
                            : rawMat.code ;
    this.rawmaterialForm.patchValue(dataToPatch);
  }

  /**
   * Lock the raw material by sending patch request
   * @param rawMat the raw material to lock
   */
  private patchRawMatToLockItWhenEditionMode(rawMat: RawMaterialDto): void {
    this.rawMatService.patch(this.userService.getEntity(), { patchAction: 'LOCK_ENTITY', rawMatIds: Array.of(rawMat.id) })
      .subscribe(() => {
        this.isReadOnly = false;
        this.checkError();
      }, () => {
        this.isReadOnly = true;
        this.checkError();
      });
  }

  /**
   * Update the header of the web page according internal data
   * @param rawMat              the raw material from what the header can be filled
   * @param redirectToInputPage if in edition mode this will indicated if the back button goes to input page or to the dashboard
   */
  private updateHeaderOfPage(rawMat: RawMaterialDto | null, redirectToInputPage: boolean): void {
    if (this.isDuplicating) {
      this.headerService.setHeaderValues({
        strongLabel: this.translate.instant('label.user.rawmaterial.duplicate.title'),
        basicLabel: ' ' + rawMat.code + ' - ' + rawMat.label,
        showGoBack: true,
        /* the route are configured in order to make it work */
        redirectionRoute: this.route.parent.snapshot
      });
    } else if (this.isEditing) {
      this.headerService.setHeaderValues({
        strongLabel: rawMat.code,
        basicLabel: ' - ' + rawMat.label,
        showGoBack: true,
        redirectionRoute:  redirectToInputPage ? `/app/rawmaterials/input/${this.idToEdit}` : `/app/rawmaterials`
      });
    } else {
      this.headerService.setHeaderValues({
        basicLabel: this.translate.instant('label.user.rawmaterial.create.title'),
        showGoBack: true,
        redirectionRoute: this.route.parent.snapshot
      });
    }
  }

  /**
   * Retrieve the reference of the raw material
   * @param rawMat the raw material from what we want the reference
   */
  private retrieveReferenceOfRawMat(rawMat: RawMaterialDto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.referenceService.get(rawMat.referenceId, { translated: true }).subscribe(reference => {
        if (reference) {
          this.rawmaterialForm.get('reference').patchValue(reference);
          this.selected = reference;
        } else {
          this.selected = null;
        }
        this.rawmaterialForm.updateValueAndValidity();
        resolve();
      }, (error) => {
        reject(new Error(error));
        /* To show built in error popin */
        throw error;
      });
    })
  }

  /**
   * Retrieve the sub entity by code and update meta data of form
   * @param code the code of the sub entity to retrieve
   */
  private retrieveSubEntityByCode(code: string): void {
    this.subEntityService.getByCode(code).subscribe(entity => {
      this.entity = entity;
      if (this.entity.logo) {
        this.entityLogo = this.domSanitizer.bypassSecurityTrustUrl(this.entity.logo);
      } else {
        this.entityLogo = undefined;
      }
      this.rawmaterialForm.get('entityCode').patchValue({ value: entity, label: `${entity.code} - ${entity.name}` });
    });
  }

  /**
   * Return the text with the prefix and check if this one is too long
   * If too long then it remove the end of the text and and suffix instead
   * @param text the text where we want to add the prefix
   * @param prefix the prefix to add
   * @param maxLength the maximum length that text + prefix should not exceed
   * @param suffixInCaseOfTooLong suffix to add in case of too long text + prefix
   * @returns the text whit prefix
   */
  private addPrefixAndTrimIfTooLong(text: string, prefix: string, maxLength: number, suffixInCaseOfTooLong: string): string {
    let textWithPrefix = prefix + text;
    if (textWithPrefix.length > maxLength) {
      textWithPrefix = textWithPrefix.slice(0, maxLength - suffixInCaseOfTooLong.length) + suffixInCaseOfTooLong;
    }
    return textWithPrefix;
  }

  checkError(): void {
    this.error = null;
    if(this.rawMaterial?.status===RawmaterialOrSampleStatus.DISABLED) {
      this.error = {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant('error.user.rawmaterial.disabled_by',
          { param: this.rawMaterial.disabledBy })
      };
    }
    else if (this.rawMaterial?.status===RawmaterialOrSampleStatus.ARCHIVED) {
      this.error = {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant('error.user.rawmaterial.archived_by',
          { param: this.rawMaterial.archivedBy })
      };
    }
    else if (this.isReadOnly && this.rawMaterial) {
      this.error = {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant('error.user.rawmaterial.locked_by',
          { param: this.rawMaterial.lockUserFullName })
      };
    }
  }

  hasFormModifications(): boolean {
    return this.rawmaterialForm.dirty;
  }

  manageRawmaterial() {
    this.check = true;
    if (this.rawmaterialForm.invalid) {
      return;
    }
    const formData = this.rawmaterialForm.value;
    const rawmaterial = new RawMaterialDto();
    rawmaterial.code = formData.code;
    rawmaterial.label = formData.label;
    rawmaterial.notes = formData.notes;
    rawmaterial.referenceId = this.selected.id;

    if (this.userService.hasRights(['U_MP_CHANGE_ENTITY'])) {
      const urnEntity = new URN();
      urnEntity.identifier = this.entity.code;
      rawmaterial.entity = urnEntity;
    } else {
      const urnEntity = new URN();
      urnEntity.identifier = this.userService.getEntity();
      rawmaterial.entity = urnEntity;
    }
    if (this.isEditing) {
      rawmaterial.id = this.idToEdit;
      rawmaterial.status = this.rawMaterial.status;

      this.rawMatService.put(this.userService.getEntity(), this.idToEdit, rawmaterial)
              .pipe(
                switchMap(() => {
                  // SI la référence a été modifiée, on envoie une 2ème requête PATCH pour forcer le statut
                  if (this.isReferenceModified) {
                    return this.rawMatService.patch(this.userService.getEntity(), {
                      rawMatIds: [rawmaterial.id],
                      patchAction: 'SAVE_CALCULATION_STATUS',
                      patchValue: CalculationStatus.BlockBeforeCalc
                    });
                  }
                  // Sinon, on passe directement à la suite sans rien faire
                  return of(null);
                })
              ).subscribe(() => {
          this.logger.info('logger.user.rawmaterial.update', `raw material with code ${rawmaterial.code} successfully updated`);

          if(this.importSemiAutoIsOnGoing){
            // Update current RM with saved values.
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.id = rawmaterial.id;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.entity = rawmaterial.entity;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.referenceId =rawmaterial.referenceId;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.referenceLabel =rawmaterial.referenceLabel;
          }
          this.router.navigate([`/app/rawmaterials/input/${rawmaterial.id}`]);
          this.toastService.toast(this.translate.instant('msg.user.rawmaterial.updated'));
        });
    } else if (this.isDuplicating) {
      this.rawMatService.duplicate(this.userService.getEntity(), this.idToDuplicate, rawmaterial)
        .subscribe((data) => {
          this.router.navigate([`/app/rawmaterials/input/${data.id}`]);
          this.toastService.toast(this.translate.instant('msg.user.rawmaterial.duplicated'));
        });
    } else {
      this.rawMatService.create(this.userService.getEntity(), rawmaterial)
        .subscribe((data) => {
          this.logger.info('logger.user.rawmaterial.create', `raw material with code ${rawmaterial.code} successfully created`);
          if(this.importSemiAutoIsOnGoing){
            // Update current RM with saved values.
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.id = data.id;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.entity = data.entity;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.referenceId =data.referenceId;
            this.importSemiautoStore.getCurrentRmValue().rawMaterial.referenceLabel =data.referenceLabel;
          }
          this.router.navigate([`/app/rawmaterials/input/${data.id}`]);
          this.toastService.toast(this.translate.instant('msg.user.rawmaterial.created'));
        });
    }

  }

  onChange(event: { reference: ReferenceDto, label: string }) {
    this.selected = event ? event.reference : null;
    this.isReferenceModified = this.rawMaterial != null && this.rawMaterial.referenceId != event.reference.id;
  }

  onChangeEntity(event: { value: EntityDto, label: string }) {
    if (!event) {
      return;
    }

    this.entity = event.value;
    if (this.entity.logo) {
      this.entityLogo = this.domSanitizer.bypassSecurityTrustUrl(this.entity.logo);
    } else {
      this.entityLogo = undefined;
    }
    this.rawmaterialForm.get('code').markAsTouched();
    this.rawmaterialForm.get('code').updateValueAndValidity();
  }

  checkCodeUnicity() {
    const filters = new RawMaterialFilterDto();
    filters.code = this.rawmaterialForm.get('code').value;
    filters.textMatchMode = 'STRICT';
    this.rawMatService.fetch(this.entity.code, filters).subscribe(res => {
      if (res?.data.some(rawmat => (rawmat.entity.identifier === this.entity.code && (!this.isEditing || this.idToEdit !== rawmat.id)))) {
        this.rawmaterialForm.get('code').setErrors({ 'unique': true });
      }
    });
  }

  search = (searchTerm: string, page: number) => {
    const filters = new ReferenceFilterDto();

    if (searchTerm) {
      filters.label = searchTerm;
      filters.code = searchTerm;
    }
    filters.page = page;
    filters.matchAny = true;

    return this.entityService.referencesFetch(this.userService.getEntity(), filters).pipe(
      map(e => e.data.map(r => {
        return { reference: r, label: `${r.code} - ${r.label}` };
      }))
    ).toPromise();
  };

  searchEntity = (searchTerm: string, page: number) => {
    const filters = new EntityFilterDto();

    if (searchTerm) {
      filters.matchAny = true;
      filters.codes = [searchTerm];
      filters.name = searchTerm;
    }
    filters.allowedFor = this.userService.getEntityDto().id;
    filters.page = page;

    return this.subEntityService.fetch(filters).pipe(
      map(e => e.data.map(entity => {
        return { value: entity, label: `${entity.code} - ${entity.name}` };
      }))
    ).toPromise();
  };

  openAdvancedReferenceSearch() {
    const dialog = this.dialogMat.open(NeqAdvancedReferencesSearchPopinComponent);
    dialog.afterClosed().subscribe(data => {
      if (data) {
        const changed = { reference: data.value, label: data.label };
        this.rawmaterialForm.get('reference').patchValue(changed);
        this.onChange(changed);
        this.rawmaterialForm.markAsDirty();
      }
    });
  }

  getFormGroup(): FormGroup {
    return this.rawmaterialForm;
  }

  ngAfterViewInit() {
    if(this.importSemiAutoIsOnGoing){
      this.rawmaterialForm.markAsDirty();
    }
  }
}
